import { skipToken } from "@reduxjs/toolkit/query"
import { useCallback, useMemo } from "react"

import useReigniteMetadata from "../lib/firebase/useReigniteMetadata"
import { useGetEventMetadataQuery, useGetMasterEventMetadataQuery } from "../api/reigniteApi"

type UseFirebaseApiSource = (masterId: number | null, eventId: number | null) => {
  apiSource: 'reignite' | 'alaska' | null
  isFetchingFirebase: boolean
  validEventId: number | null
  validMasterId: number | null
}

const useValidIds = (masterId: number | null, eventId: number | null) => {
  const idsAlreadyExist = !!(masterId && eventId)

  const {
    data: masterReigniteData,
    isFetching: isMasterReigniteFetching,
  } = useGetMasterEventMetadataQuery(masterId ?? skipToken, { skip: idsAlreadyExist })

  const currentEventIdFromReigniteApi = useMemo(() => masterReigniteData?.currentEvent?.id || null, [masterReigniteData])

  const {
    data: eventReigniteData,
    isFetching: isEventReigniteFetching,
  } = useGetEventMetadataQuery(eventId ?? skipToken, { skip: idsAlreadyExist })

  const currentMasterIdFromReigniteApi = useMemo(() => eventReigniteData?.masterId || null, [eventReigniteData]) 
  
  if (idsAlreadyExist) {
    return { validMasterId: masterId, validEventId: eventId }
  }

  if (isMasterReigniteFetching || isEventReigniteFetching) {
    return { isValidatingIds: true, validMasterId: null, validEventId: null }
  }

  if (masterId && currentEventIdFromReigniteApi) {
    return { validMasterId: masterId, validEventId: currentEventIdFromReigniteApi }
  }

  if (currentMasterIdFromReigniteApi && eventId) {
    return { validMasterId: currentMasterIdFromReigniteApi, validEventId: eventId }
  }

  return { validMasterId: null, validEventId: null }
}

const useFirebaseApiSource: UseFirebaseApiSource = (masterId, eventId) => {
  const { validMasterId, validEventId, isValidatingIds } = useValidIds(masterId, eventId)

  const { isFetchingFirebaseConfig, reigniteApiUsageConfig } = useReigniteMetadata()

  const checkIfReigniteCanBeUsed = useCallback((masterId: number, eventId: number, config: NonNullable<typeof reigniteApiUsageConfig>) => {
    if (!(masterId in config)) {
      return false
    }

    const master = config[masterId]

    const isMasterAllowed = master.blacklist.length === 0 && master.whitelist.length === 0
    const isEventAllowed = master.whitelist.includes(eventId) && !master.blacklist.includes(eventId)

    return isMasterAllowed || isEventAllowed
  }, [])

  const apiSource = useMemo(() => {
    if (!reigniteApiUsageConfig || !validMasterId || !validEventId) {
      return null
    }

    if (checkIfReigniteCanBeUsed(validMasterId, validEventId, reigniteApiUsageConfig)) {
      return 'reignite'
    }

    return 'alaska'
  }, [checkIfReigniteCanBeUsed, reigniteApiUsageConfig, validEventId, validMasterId])

  return {
    apiSource,
    isFetchingFirebase: isFetchingFirebaseConfig || !!isValidatingIds,
    validEventId,
    validMasterId,
  }
}

export { useFirebaseApiSource }